import { type CSSProperties, useRef, useCallback } from "react";

type Options = {
  delay?: number;
  cancelDistance?: number;
};

export function useLongPress(
  onLongPress: () => void,
  onTap?: () => void,
  { delay = 500, cancelDistance = 10 }: Options = {},
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggered = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);

  const handleStart = useCallback(
    (e: TouchEvent | any) => {
      triggered.current = false;

      const touch = e.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;

      timeoutRef.current = setTimeout(() => {
        triggered.current = true;
        onLongPress();
      }, delay);
    },
    [onLongPress, delay],
  );

  const handleMove = useCallback(
    (e: TouchEvent | any) => {
      if (!timeoutRef.current) return;

      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - startX.current);
      const dy = Math.abs(touch.clientY - startY.current);

      if (dx > cancelDistance || dy > cancelDistance) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    },
    [cancelDistance],
  );

  const handleEnd = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!triggered.current) {
      onTap?.();
    }
  }, [onTap]);

  return {
    onTouchStart: handleStart,
    onTouchMove: handleMove,
    onTouchEnd: handleEnd,
    onTouchCancel: handleEnd,
    style: {
      WebkitTouchCallout: "none",
      WebkitUserSelect: "none",
      userSelect: "none",
    } as CSSProperties,
  };
}
